import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Person,
  LocalHospital,
  MedicalInformation,
  AttachMoney,
  CalendarToday,
  AccessTime,
  AddCard,
  AttachFile,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getRecords, PaymentState, deleteRecord, MedicalRecord } from '../api/api';
import RecordDetailDialog from './RecordDetailDialog';
// import DebugPanel from './DebugPanel';

const RecordsList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [recordType, setRecordType] = useState('');
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    record: MedicalRecord | null;
  }>({
    open: false,
    record: null,
  });
  const [deleting, setDeleting] = useState(false);
  // Payment state filter. Kept separate from record_type, which is a category
  // (and one that only ever takes a single value in this data).
  const [paymentState, setPaymentState] = useState<string>('');
  // Detail dialog: payment confirmation and the surgery paperwork, neither of
  // which had any interface before.
  const [detailRecord, setDetailRecord] = useState<MedicalRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 500 }; // Increase limit to see all records
      if (searchTerm) params.search = searchTerm;
      if (recordType) params.record_type = recordType;
      if (paymentState) params.payment_state = paymentState;
      
      console.log('Fetching records with params:', params);
      const data = await getRecords(params);
      console.log('Received records:', data?.length || 0, 'records');
      setRecords(data || []);
      
      if (!data || data.length === 0) {
        console.warn('No records returned from API');
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      setError('Failed to load records: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRecords();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, recordType, paymentState]);

  /**
   * Traffic light for payment state.
   *
   * Colours follow the alert windows the practice asked for: green under
   * three months, amber three to six, red past six, dark red past a year.
   * The state itself is computed in the database, so this only maps it to a
   * presentation -- the UI never decides what counts as overdue.
   */
  const paymentStateDisplay = (
    state: PaymentState | null,
    days: number | null
  ): { label: string; color: string; bg: string; title: string } => {
    const age = days != null ? `${days} days since procedure` : '';
    switch (state) {
      case 'paid':
        return { label: 'Pago', color: '#065f46', bg: '#d1fae5', title: `Payment confirmed. ${age}` };
      case 'scheduled':
        return { label: 'Agendado', color: '#374151', bg: '#f3f4f6', title: 'Procedure is in the future' };
      case 'awaiting':
        return { label: '< 3 meses', color: '#065f46', bg: '#d1fae5', title: `Awaiting payment. ${age}` };
      case 'due':
        return { label: '3-6 meses', color: '#92400e', bg: '#fef3c7', title: `Payment due. ${age}` };
      case 'overdue':
        return { label: '6-12 meses', color: '#991b1b', bg: '#fee2e2', title: `Overdue. ${age}` };
      case 'critical':
        return { label: '> 12 meses', color: '#ffffff', bg: '#991b1b', title: `Critical - over a year unpaid. ${age}` };
      default:
        return { label: '-', color: '#6b7280', bg: '#f3f4f6', title: 'No payment state' };
    }
  };

  const handleDeleteClick = (record: MedicalRecord) => {
    setDeleteDialog({
      open: true,
      record,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.record) return;
    
    setDeleting(true);
    try {
      await deleteRecord(deleteDialog.record.id);
      setSuccess('Record deleted successfully');
      fetchRecords();
    } catch (error) {
      setError('Failed to delete record');
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, record: null });
    }
  };

  const handleAddPayment = (record: MedicalRecord) => {
    // Navigate to edit the record with payment section enabled
    navigate(`/records/edit/${record.id}?addPayment=true`);
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const truncateText = (text: string | null, maxLength: number = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const filteredRecords = records;

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        <Typography variant="body2" color="text.secondary">
          Loading medical records...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Medical Records
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Manage and view all medical appointments and procedures
          </Typography>
        </Box>

        <Card 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="center">
              <TextField
                placeholder="Search patients, doctors, hospitals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  }
                }}
              />
              
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={recordType}
                  label="Filter by Type"
                  onChange={(e) => setRecordType(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Records</MenuItem>
                  <MenuItem value="appointment">Appointments</MenuItem>
                  <MenuItem value="payment">Payments</MenuItem>
                </Select>
              </FormControl>

              {/* Payment state. The windows match the alert thresholds so the
                  filter and the traffic light cannot disagree. */}
              <FormControl sx={{ minWidth: 190 }}>
                <InputLabel>Pagamento</InputLabel>
                <Select
                  value={paymentState}
                  label="Pagamento"
                  onChange={(e) => setPaymentState(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="unpaid">Nao pago (todos)</MenuItem>
                  <MenuItem value="paid">Pago</MenuItem>
                  <MenuItem value="awaiting">Aguardando (&lt; 3 meses)</MenuItem>
                  <MenuItem value="due">Vencendo (3-6 meses)</MenuItem>
                  <MenuItem value="overdue">Atrasado (6-12 meses)</MenuItem>
                  <MenuItem value="critical">Critico (&gt; 12 meses)</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <Tooltip title="Refresh data">
                  <IconButton
                    onClick={fetchRecords}
                    sx={{ 
                      backgroundColor: alpha('#6366f1', 0.1),
                      '&:hover': { backgroundColor: alpha('#6366f1', 0.2) }
                    }}
                  >
                    <Refresh sx={{ color: '#6366f1' }} />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/records/new')}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    }
                  }}
                >
                  Add New Record
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Paper 
          sx={{ 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#6366f1', 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday sx={{ fontSize: 16, color: '#6366f1' }} />
                      Data
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTime sx={{ fontSize: 16, color: '#6366f1' }} />
                      Hora
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 16, color: '#6366f1' }} />
                      Paciente
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <MedicalInformation sx={{ fontSize: 16, color: '#6366f1' }} />
                      Cirurgia / Procedimento
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalHospital sx={{ fontSize: 16, color: '#6366f1' }} />
                      Hospital
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 16, color: '#6366f1' }} />
                      Cirurgião
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    Fonte Pagadora
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachMoney sx={{ fontSize: 16, color: '#6366f1' }} />
                      Valor Pago
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Arquivo Origem</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((record, index) => (
                  <TableRow 
                    key={record.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha('#6366f1', 0.02),
                        transition: 'all 0.2s ease'
                      },
                      borderBottom: index === filteredRecords.length - 1 ? 'none' : '1px solid #e2e8f0'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(record.record_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {record.record_time || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar 
                          sx={{ 
                            width: 28, 
                            height: 28, 
                            backgroundColor: '#6366f1',
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        >
                          {record.patient_name ? record.patient_name.charAt(0).toUpperCase() : 'P'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {record.patient_name || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" noWrap>
                        {record.procedure_name || record.procedure_description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {record.hospital_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {record.doctor_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.insurance_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color={record.amount_paid ? '#10b981' : 'text.secondary'}
                      >
                        {formatCurrency(record.amount_paid)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Traffic light plus the payment lag, which is the
                          "quanto tempo entre fazer exame e receber" figure. */}
                      {(() => {
                        const d = paymentStateDisplay(record.payment_state, record.days_since_procedure);
                        return (
                          <Tooltip title={d.title}>
                            <Box>
                              <Chip
                                size="small"
                                label={d.label}
                                sx={{
                                  bgcolor: d.bg,
                                  color: d.color,
                                  fontWeight: 700,
                                  minHeight: 24,
                                }}
                              />
                              {record.payment_confirmed && record.payment_lag_days != null && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {record.payment_lag_days}d ate receber
                                </Typography>
                              )}
                              {!record.payment_confirmed && record.amount_billed_net != null && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  liquido {formatCurrency(record.amount_billed_net)}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {record.source_file || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit record">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/records/edit/${record.id}`)}
                            sx={{
                              backgroundColor: alpha('#6366f1', 0.1),
                              '&:hover': { backgroundColor: alpha('#6366f1', 0.2) }
                            }}
                          >
                            <Edit fontSize="small" sx={{ color: '#6366f1' }} />
                          </IconButton>
                        </Tooltip>
                        {(!record.amount_paid || record.amount_paid <= 0) && (
                          <Tooltip title="Add payment">
                            <IconButton
                              size="small"
                              onClick={() => handleAddPayment(record)}
                              sx={{
                                backgroundColor: alpha('#10b981', 0.1),
                                '&:hover': { backgroundColor: alpha('#10b981', 0.2) }
                              }}
                            >
                              <AddCard fontSize="small" sx={{ color: '#10b981' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Documentos e pagamento">
                          <IconButton
                            size="small"
                            onClick={() => setDetailRecord(record)}
                            sx={{
                              backgroundColor: alpha('#0ea5e9', 0.1),
                              '&:hover': { backgroundColor: alpha('#0ea5e9', 0.2) }
                            }}
                          >
                            <AttachFile fontSize="small" sx={{ color: '#0ea5e9' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete record">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(record)}
                            sx={{
                              backgroundColor: alpha('#ef4444', 0.1),
                              '&:hover': { backgroundColor: alpha('#ef4444', 0.2) }
                            }}
                          >
                            <Delete fontSize="small" sx={{ color: '#ef4444' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {filteredRecords.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <MedicalInformation sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
              No records found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              {searchTerm 
                ? 'No records match your search criteria. Try adjusting your search terms or filters.' 
                : 'No medical records are available. Check if the backend is running and data is imported.'
              }
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchRecords}
                sx={{ borderRadius: 2 }}
              >
                Retry Loading
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/records/new')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  }
                }}
              >
                Add New Record
              </Button>
            </Stack>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, record: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this medical record? This action cannot be undone.
              <br />
              <br />
              <strong>Patient:</strong> {deleteDialog.record?.patient_name || 'N/A'}
              <br />
              <strong>Date:</strong> {deleteDialog.record ? formatDate(deleteDialog.record.record_date) : 'N/A'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialog({ open: false, record: null })}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <RecordDetailDialog
          record={detailRecord}
          open={detailRecord !== null}
          onClose={() => setDetailRecord(null)}
          onChanged={fetchRecords}
        />

        {/* Success/Error Messages */}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {error}
          </Alert>
        )}
        
        {/* <DebugPanel /> */}
      </Box>
    </Fade>
  );
};

export default RecordsList;